import { DOCUMENT, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, Component, ChangeDetectionStrategy, Inject, HostBinding, Injectable, Directive, Input, NgModule } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { DomPortalOutlet, PortalInjector, ComponentPortal } from '@angular/cdk/portal';

const SI_LOADING_IS_GLOBAL = new InjectionToken('si.loading.is.global');

class SiLoadingSpinnerComponent {
    constructor(isGlobal) {
        this.isGlobal = isGlobal;
        // https://stackoverflow.com/a/56248973
        this.nothing = true;
    }
}
SiLoadingSpinnerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.7", ngImport: i0, type: SiLoadingSpinnerComponent, deps: [{ token: SI_LOADING_IS_GLOBAL }], target: i0.ɵɵFactoryTarget.Component });
SiLoadingSpinnerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.7", type: SiLoadingSpinnerComponent, selector: "si-loading-spinner", host: { properties: { "@transition": "this.nothing" } }, ngImport: i0, template: "<div class=\"loading-spinner\" [class.global-spinner]=\"isGlobal\">\n    <span class=\"visually-hidden\">Loading...</span>\n    <div class=\"spinner-container\">\n        <div>\n            <div class=\"spinner-border\"></div>\n        </div>\n    </div>\n</div>\n", styles: [""], animations: [trigger('transition', [
            state('true', style({ opacity: 1 })),
            transition('void => *', [style({ opacity: 0 }), animate('100ms ease-in')]),
            transition('* => void', [animate('100ms ease-out', style({ opacity: 0 }))]),
        ])], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.7", ngImport: i0, type: SiLoadingSpinnerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-loading-spinner', changeDetection: ChangeDetectionStrategy.OnPush, animations: [trigger('transition', [
                            state('true', style({ opacity: 1 })),
                            transition('void => *', [style({ opacity: 0 }), animate('100ms ease-in')]),
                            transition('* => void', [animate('100ms ease-out', style({ opacity: 0 }))]),
                        ])], template: "<div class=\"loading-spinner\" [class.global-spinner]=\"isGlobal\">\n    <span class=\"visually-hidden\">Loading...</span>\n    <div class=\"spinner-container\">\n        <div>\n            <div class=\"spinner-border\"></div>\n        </div>\n    </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [SI_LOADING_IS_GLOBAL]
                }] }]; }, propDecorators: { nothing: [{
                type: HostBinding,
                args: ['@transition']
            }] } });

class SiLoadingElement {
    constructor(domPortal, componentPortal, isGlobal) {
        this.domPortal = domPortal;
        this.componentPortal = componentPortal;
        this.isGlobal = isGlobal;
        this.stackCount = 0;
    }
    startLoading() {
        if (this.stackCount === 0) {
            this.componentPortal.attach(this.domPortal);
        }
        this.stackCount++;
    }
    stopLoading() {
        if (this.stackCount) {
            if (this.stackCount === 1) {
                this.componentPortal.detach();
            }
            this.stackCount--;
        }
    }
}
class SiLoadingService {
    constructor(componentFactoryResolver, injector, appRef, document) {
        this.componentFactoryResolver = componentFactoryResolver;
        this.injector = injector;
        this.appRef = appRef;
        this.document = document;
        this.loadingCounts = new Map();
    }
    /**
     * Starts a new loading spinner. If there is already a spinner attached to the target no new spinner will be created.
     *
     * @param siLoadingOptions if no options or no target are provided the spinner will be globally
     */
    startLoading(siLoadingOptions) {
        const { target } = siLoadingOptions ?? { target: this.document.body };
        let loadingElement = this.loadingCounts.get(target);
        if (!loadingElement) {
            loadingElement = this.createPortal(target);
            this.loadingCounts.set(target, loadingElement);
        }
        loadingElement.startLoading();
        return { stopLoading: () => loadingElement.stopLoading(), target };
    }
    /**
     * Stops a loading spinner. If {@link startLoading} was called more than once for a specific target {@link stopLoading} must be called
     * as many times as {@link startLoading} to hide the spinner. More calls are ignored
     *
     * @param siLoadingReference if no options or no target are provided the global spinner will be used
     */
    stopLoading(siLoadingReference) {
        const { target, stopLoading } = siLoadingReference ?? { target: this.document.body };
        if (stopLoading) {
            stopLoading();
        }
        else {
            const loadingElement = this.loadingCounts.get(target);
            if (loadingElement) {
                loadingElement.stopLoading();
            }
        }
    }
    createPortal(target = this.document.body) {
        const additionalInjectors = new WeakMap();
        const isGlobal = target === this.document.body;
        additionalInjectors.set(SI_LOADING_IS_GLOBAL, isGlobal);
        return new SiLoadingElement(new DomPortalOutlet(target, this.componentFactoryResolver, this.appRef, new PortalInjector(this.injector, additionalInjectors)), new ComponentPortal(SiLoadingSpinnerComponent), isGlobal);
    }
}
SiLoadingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.7", ngImport: i0, type: SiLoadingService, deps: [{ token: i0.ComponentFactoryResolver }, { token: i0.Injector }, { token: i0.ApplicationRef }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
SiLoadingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.7", ngImport: i0, type: SiLoadingService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.7", ngImport: i0, type: SiLoadingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i0.ComponentFactoryResolver }, { type: i0.Injector }, { type: i0.ApplicationRef }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

class SiLoadingSpinnerDirective {
    constructor(elementRef, siLoadingService) {
        this.elementRef = elementRef;
        this.siLoadingService = siLoadingService;
        this.position = 'relative';
    }
    set siLoading(value) {
        if (value) {
            this.showSpinner();
        }
        else {
            this.hideSpinner();
        }
    }
    showSpinner() {
        this.loadingRef = this.siLoadingService.startLoading({ target: this.elementRef.nativeElement });
    }
    hideSpinner() {
        if (this.loadingRef) {
            this.loadingRef.stopLoading();
        }
    }
}
SiLoadingSpinnerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.7", ngImport: i0, type: SiLoadingSpinnerDirective, deps: [{ token: i0.ElementRef }, { token: SiLoadingService }], target: i0.ɵɵFactoryTarget.Directive });
SiLoadingSpinnerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.7", type: SiLoadingSpinnerDirective, selector: "[siLoading]", inputs: { siLoading: "siLoading" }, host: { properties: { "style.position": "this.position" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.7", ngImport: i0, type: SiLoadingSpinnerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siLoading]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: SiLoadingService }]; }, propDecorators: { position: [{
                type: HostBinding,
                args: ['style.position']
            }], siLoading: [{
                type: Input
            }] } });

class SiLoadingSpinnerModule {
}
SiLoadingSpinnerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.7", ngImport: i0, type: SiLoadingSpinnerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiLoadingSpinnerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.7", ngImport: i0, type: SiLoadingSpinnerModule, declarations: [SiLoadingSpinnerComponent, SiLoadingSpinnerDirective], imports: [CommonModule], exports: [SiLoadingSpinnerComponent, SiLoadingSpinnerDirective] });
SiLoadingSpinnerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.7", ngImport: i0, type: SiLoadingSpinnerModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.7", ngImport: i0, type: SiLoadingSpinnerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiLoadingSpinnerComponent, SiLoadingSpinnerDirective],
                    imports: [
                        CommonModule,
                    ],
                    exports: [SiLoadingSpinnerComponent, SiLoadingSpinnerDirective],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SiLoadingService, SiLoadingSpinnerComponent, SiLoadingSpinnerDirective, SiLoadingSpinnerModule };
